import React from "react";
import { FileText, MessageSquare, User, Building2, Calendar, CheckCircle, XCircle, Clock, Download } from "lucide-react";
import type { TaskSubmission } from "../../../types/task";
import Modal from "../../../components/ui/Modal";

interface ViewSubmissionModalProps {
  isOpen: boolean;
  submission: TaskSubmission | null;
  onClose: () => void;
}

export const ViewSubmissionModal: React.FC<ViewSubmissionModalProps> = ({ isOpen, submission, onClose }) => {
  if (!submission) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Bilinmiyor";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getStatusBadge = () => {
    switch (submission.status) {
      case "APPROVED":
        return (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
            <CheckCircle className="w-4 h-4" />
            Onaylandı
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
            <XCircle className="w-4 h-4" />
            Reddedildi
          </span>
        );
      case "SUBMITTED":
        return (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
            <Clock className="w-4 h-4" />
            İncelemede
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm">
            <Clock className="w-4 h-4" />
            Bekliyor
          </span>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={submission.taskTitle} size="lg">
      <div className="px-6 pt-4 pb-6 space-y-5">
        <div className="flex items-center justify-center pb-4 border-b border-gray-200">{getStatusBadge()}</div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gönderen</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">{submission.userName}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Departman</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
              <Building2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">{submission.userDepartment}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gönderim Tarihi</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-900">{formatDate(submission.submittedAt)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {submission.fileUrl ? "Gönderilen Dosya" : "Gönderilen İçerik"}
            </label>

            {submission.fileUrl ? (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{submission.fileName || "dosya.pdf"}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{formatFileSize(submission.fileSize)}</p>
                  </div>
                  <button
                    onClick={() => window.open(submission.fileUrl, "_blank")}
                    className="flex-shrink-0 px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    İndir
                  </button>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{submission.content || "İçerik bulunamadı"}</p>
              </div>
            )}
          </div>

          {submission.reviewNote && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">İnceleme Notu</label>
              <div
                className={`border rounded-md p-4 ${submission.status === "REJECTED"
                    ? "bg-red-50 border-red-200"
                    : submission.status === "APPROVED"
                      ? "bg-green-50 border-green-200"
                      : "bg-gray-50 border-gray-200"
                  }`}>
                <p className="text-sm text-gray-800 leading-relaxed">{submission.reviewNote}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors">
          Kapat
        </button>
      </div>
    </Modal>
  );
};